import dayjs from "dayjs";
import type {
  EditorDocument,
  EnrichedEditorDocument,
  EnrichedMessagePage,
  EnrichedResult,
  Message,
  MessagePage,
  Result,
} from "./types";

export const STAGE_LABELS = {
  reading: "Reading your payroll information",
  planning: "Working out the change",
  editing: "Making the change",
  checking: "Checking the proposed rules",
  calculating: "Comparing payroll calculations",
  saving: "Saving the proposal",
  documenting: "Updating your written rules",
} as const;

const elapsedLabel = (startedAt: string | undefined) => {
  const started = startedAt ? dayjs(startedAt) : null;
  if (!started?.isValid()) return "";
  const minutes = dayjs().diff(started, "minute");
  return minutes < 1 ? "" : ` · ${minutes} min`;
};

const progressLabel = (
  request: Message | null | undefined,
  startedAt: string | undefined,
) => {
  if (request?.status === "queued") return "Waiting to start…";
  const stage = request?.progress?.stage;
  const label =
    (stage && STAGE_LABELS[stage as keyof typeof STAGE_LABELS]) ??
    STAGE_LABELS.reading;
  return `${label}…${elapsedLabel(startedAt)}`;
};

const issueLabels: Record<string, string> = {
  candidate_execution_failed: "The proposed calculation could not finish.",
  requested_sheet_not_found: "A requested payroll sheet is missing.",
  statement_readiness_regression:
    "A PayPack needs correction after this change.",
  candidate_statement_not_ready: "A proposed PayPack is not ready.",
  requested_statement_target_not_found:
    "A requested employee is missing from the results.",
  roster_override_did_not_change_output:
    "The roster change did not change the employee's result.",
};

const cellValue = (value: string | number | boolean | null | undefined) => {
  if (value == null) return "Empty";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  // Never show a formula when a computed value is unavailable.
  if (typeof value === "string" && value.startsWith("="))
    return "Not calculated";
  return String(value);
};

/** Warnings arrive as strings or as objects carrying one. */
const warningText = (warning: string | { message?: string }) =>
  (typeof warning === "string" ? warning : warning.message)?.trim() ?? "";

const enrichResult = (result: Result): EnrichedResult => ({
  ...result,
  issueMessages: (result.acceptance?.issues ?? []).map(
    (issue) =>
      issueLabels[issue.code ?? ""] ??
      "A payroll check needs correction. Ask the agent for details.",
  ),
  governanceMessages: (result.governance?.warnings ?? [])
    .map(warningText)
    .filter(Boolean),
  incomplete: !!(
    result.truncation ||
    result.comparison?.sheets_truncated ||
    result.statement_comparison?.targets_truncated ||
    result.statement_comparison?.statement_details_truncated
  ),
  changedSheets: (result.comparison?.sheets ?? [])
    .filter((sheet) => sheet.state !== "unchanged")
    .map((sheet) => ({
      ...sheet,
      label:
        sheet.state === "added"
          ? "Added"
          : sheet.state === "removed"
            ? "Removed"
            : "Changed",
      // The workbook's own labels, not the cell address: "F107" tells an owner
      // nothing, "Adam N · Total pay" tells them whose pay moved.
      rows: (sheet.change_samples ?? []).map((sample) => ({
        who: sample.row_label ?? sample.address,
        what: sample.column_label ?? "",
        before: cellValue(sample.baseline?.value),
        after: cellValue(sample.candidate?.value),
      })),
    })),
  statements: (result.statement_comparison?.statements ?? []).map(
    (statement) => ({
      ...statement,
      label:
        statement.candidate?.ready === false
          ? "Needs correction"
          : statement.changed
            ? "Changed"
            : "Unchanged",
    }),
  ),
});

/**
 * A request that ends in a clarification produces both the structured question,
 * which carries the answer buttons, and a written answer restating it. Showing
 * both asks the owner the same thing twice, so the restatement is dropped.
 */
const isRestatedClarification = (message: Message, page: MessagePage) =>
  message.role === "assistant" &&
  page.messages.some(
    (other) =>
      other.result?.kind === "clarification" &&
      other.request_id === message.request_id,
  );

/**
 * The agent runs the payroll repeatedly while it works — edit, check, correct —
 * and each run reported its own card, so one request left three near-identical
 * "336 numbers changed across 23 sheets" blocks in the transcript. Only the last
 * run of a request describes what was actually saved; the earlier ones are
 * working, like the validation and figure-check results already dropped.
 */
const isSupersededComparison = (message: Message, page: MessagePage) =>
  message.result?.kind === "calculation_comparison" &&
  page.messages.some(
    (other) =>
      other.result?.kind === "calculation_comparison" &&
      other.request_id === message.request_id &&
      other.sequence > message.sequence,
  );

/**
 * A clean save is already stated by the written answer ("Saved as a proposal:
 * …"), so the card repeated it under a heading. It earns its place only when it
 * carries something the answer does not: that nothing was saved, or a problem
 * to act on.
 */
const isRedundantSave = (message: Message, page: MessagePage) =>
  message.result?.kind === "saved_changes" &&
  message.result.applied === true &&
  !message.result.error &&
  !message.result.governance?.warnings?.length &&
  page.messages.some(
    (other) =>
      other.role === "assistant" &&
      other.request_id === message.request_id &&
      !!other.content.trim(),
  );

export const enrichMessagePage = (page: MessagePage): EnrichedMessagePage => ({
  ...page,
  messages: page.messages
    .filter((message) => !isRestatedClarification(message, page))
    .filter((message) => !isSupersededComparison(message, page))
    .filter((message) => !isRedundantSave(message, page))
    .map((message) => ({
      ...message,
      result: message.result ? enrichResult(message.result) : undefined,
    })),
  isActive:
    !!page.workspace.active_request ||
    page.latest_request?.status === "queued" ||
    page.latest_request?.status === "running",
  progressLabel: progressLabel(
    page.latest_request,
    page.workspace.active_request?.started_at,
  ),
  // The assistant has asked something and stopped. Nothing is running, and the
  // next move is the reader's. It is the last message CARRYING A RESULT that
  // decides: a clarification is followed by the written restatement, so the
  // last message is prose and says nothing about what the request ended as.
  awaitingAnswer:
    !page.workspace.active_request &&
    page.messages.filter((message) => message.result).at(-1)?.result?.kind ===
      "clarification",
});

const documentStatusLabels = {
  ready: "Ready",
  pending: "Reading file…",
  extraction_failed: "File could not be read. Remove it and upload a new copy.",
} as const;

export const enrichEditorDocuments = (
  documents: EditorDocument[],
): EnrichedEditorDocument[] =>
  documents.map((document) => ({
    ...document,
    statusLabel: documentStatusLabels[document.status],
  }));
